/**
 * InsightPro™ — Governance Pages  v4
 * FIX MEDIUM 12: Advisory and ActionTracking show visible error messages on failure.
 * FIX BLOCKER 6: Uses note.PdfUrl (as returned by data-api v4) for PDF button.
 * FIX BLOCKER 7: Renders note.Observations and note.Actions arrays.
 */
import React, { useState, useEffect } from 'react';
import { SectionHeader, Card, CardTitle, Flag, MetricRow } from '../components/UI';
import { fetchAdvisoryNotes, fetchActionItems, updateActionStatus } from '../utils/api';

function ApiError({ message }) {
  return (
    <div style={{ padding:'12px 16px', background:'rgba(212,95,95,0.07)', border:'1px solid rgba(212,95,95,0.2)', borderRadius:2 }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--red)', marginBottom:4 }}>DATA LOAD ERROR</div>
      <div style={{ fontSize:11, color:'var(--text-muted)' }}>{message || 'Could not load data. Check function logs.'}</div>
    </div>
  );
}

export function Advisory() {
  const [notes,    setNotes]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchAdvisoryNotes()
      .then(data => { setNotes(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  return (
    <div style={{ animation:'fadeUp 0.4s ease both' }}>
      <SectionHeader title="Monthly Advisory Notes" />
      <div style={{ display:'grid', gridTemplateColumns: selected ? '280px 1fr' : '1fr', gap:20 }}>

        {/* Note list */}
        <div>
          {loading && <div style={{ color:'var(--text-muted)', fontFamily:'var(--font-mono)', fontSize:11 }}>Loading...</div>}
          {error   && <ApiError message={error} />}
          {!loading && !error && notes.length === 0 && (
            <div style={{ color:'var(--text-muted)', fontSize:12, padding:16 }}>
              No advisory notes have been added yet. Notes are uploaded by IGCS after each monthly session.
            </div>
          )}
          {notes.map(note => (
            <div key={note.NoteKey} onClick={() => setSelected(note)}
              style={{
                padding:'12px 16px', cursor:'pointer', marginBottom:8,
                background: selected?.NoteKey === note.NoteKey ? 'var(--gold-dim)' : 'var(--surface-2)',
                border: `1px solid ${selected?.NoteKey === note.NoteKey ? 'var(--border-gold)' : 'var(--border)'}`,
              }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <div style={{ fontSize:13, fontWeight:500, color:'var(--gold-light)' }}>{note.PeriodLabel}</div>
                {note.IsSignedOff
                  ? <Flag type="ok">Signed Off</Flag>
                  : <Flag type="warning">Draft</Flag>
                }
              </div>
              <div style={{ fontSize:11, color:'var(--text-muted)' }}>{note.Title}</div>
              <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--text-muted)', marginTop:4 }}>Session: {note.SessionDate}</div>
            </div>
          ))}
        </div>

        {/* Note detail */}
        {selected && (
          <Card>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
              <div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:20, color:'var(--gold-light)' }}>{selected.PeriodLabel}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--text-muted)', marginTop:4 }}>{selected.Title}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background:'transparent', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:16 }}>✕</button>
            </div>

            <div style={{ marginBottom:20 }}>
              <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:8 }}>Executive Summary</div>
              <div style={{ fontSize:13, color:'var(--text-sec)', lineHeight:1.7 }}>{selected.ExecutiveSummary || 'No summary available.'}</div>
            </div>

            {/* FIX BLOCKER 7: Render Observations array */}
            {Array.isArray(selected.Observations) && selected.Observations.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:10 }}>Key Observations</div>
                {selected.Observations.map((obs, i) => (
                  <div key={i} style={{ display:'flex', gap:10, marginBottom:10, alignItems:'flex-start' }}>
                    <div style={{ width:4, height:4, borderRadius:'50%', background:'var(--gold)', marginTop:6, flexShrink:0 }} />
                    <div style={{ fontSize:12, color:'var(--text-sec)', lineHeight:1.6 }}>{typeof obs === 'string' ? obs : obs.text || JSON.stringify(obs)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* FIX BLOCKER 7: Render Actions array */}
            {Array.isArray(selected.Actions) && selected.Actions.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:10 }}>Recommended Actions</div>
                {selected.Actions.map((action, i) => (
                  <div key={i} style={{ display:'flex', gap:10, marginBottom:8, padding:'8px 12px', background:'var(--gold-dim)', border:'1px solid var(--border-gold)' }}>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--gold)', minWidth:20 }}>{String(i+1).padStart(2,'0')}</div>
                    <div style={{ fontSize:12, color:'var(--text-sec)' }}>{typeof action === 'string' ? action : action.text || JSON.stringify(action)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* FIX BLOCKER 6: uses note.PdfUrl (mapped by data-api v4) */}
            {selected.PdfUrl && (
              <a href={selected.PdfUrl} target="_blank" rel="noopener noreferrer" style={{
                display:'inline-flex', alignItems:'center', gap:8,
                fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase',
                color:'var(--gold)', padding:'8px 18px', border:'1px solid var(--border-gold)',
                textDecoration:'none',
              }}>
                ↓ Download Executive PDF Pack
              </a>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

export function ActionTracking() {
  const [actions, setActions]  = useState([]);
  const [loading, setLoading]  = useState(true);
  const [error,   setError]    = useState(null);

  useEffect(() => {
    fetchActionItems()
      .then(data => { setActions(data); setLoading(false); })
      .catch(err  => { setError(err.message); setLoading(false); });
  }, []);

  const handleStatusChange = async (actionKey, newStatus) => {
    try {
      await updateActionStatus(actionKey, newStatus);
      setActions(prev => prev.map(a => a.ActionKey === actionKey ? { ...a, Status: newStatus } : a));
    } catch (err) {
      console.error('Status update failed:', err.message);
      alert('Could not update status. Please try again.');
    }
  };

  const summary = {
    total:      actions.length,
    open:       actions.filter(a => a.Status === 'OPEN').length,
    inProgress: actions.filter(a => a.Status === 'IN_PROGRESS').length,
    done:       actions.filter(a => a.Status === 'DONE').length,
    overdue:    actions.filter(a => a.IsOverdue).length,
  };

  return (
    <div style={{ animation:'fadeUp 0.4s ease both' }}>
      <SectionHeader title="Action Tracking" />

      {loading && <div style={{ color:'var(--text-muted)', fontFamily:'var(--font-mono)', fontSize:11 }}>Loading actions...</div>}
      {error   && <ApiError message={error} />}

      {!loading && !error && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
            {[
              { label:'Total Actions', value: summary.total,      color:'var(--text-primary)' },
              { label:'In Progress',   value: summary.inProgress, color:'var(--gold)' },
              { label:'Completed',     value: summary.done,       color:'var(--green)' },
              { label:'Overdue',       value: summary.overdue,    color: summary.overdue > 0 ? 'var(--red)' : 'var(--text-muted)' },
            ].map(s => (
              <Card key={s.label} style={{ textAlign:'center' }}>
                <div style={{ fontFamily:'var(--font-display)', fontSize:32, color: s.color, lineHeight:1 }}>{s.value}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--text-muted)', marginTop:6, letterSpacing:'0.1em' }}>{s.label}</div>
              </Card>
            ))}
          </div>

          {actions.length === 0 ? (
            <div style={{ color:'var(--text-muted)', fontSize:12, padding:16 }}>No action items recorded yet.</div>
          ) : (
            <Card>
              <CardTitle>Action Items</CardTitle>
              {actions.map((a, i) => (
                <div key={a.ActionKey} style={{ padding:'12px 0', borderBottom: i < actions.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
                    <div style={{ flex:1, minWidth:200 }}>
                      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4 }}>
                        <Flag type={a.Priority === 'HIGH' ? 'critical' : a.Priority === 'MEDIUM' ? 'warning' : 'info'}>{a.Priority}</Flag>
                        {a.IsOverdue && <Flag type="critical">OVERDUE</Flag>}
                        <span style={{ fontSize:12.5, fontWeight:500, color:'var(--text-primary)' }}>{a.Title}</span>
                      </div>
                      {a.Description && <div style={{ fontSize:11.5, color:'var(--text-muted)', marginBottom:4, lineHeight:1.4 }}>{a.Description}</div>}
                      <div style={{ fontFamily:'var(--font-mono)', fontSize:9.5, color:'var(--text-muted)', display:'flex', gap:16 }}>
                        {a.AssignedTo && <span>→ {a.AssignedTo}</span>}
                        {a.DueDate    && <span>Due: {a.DueDate}</span>}
                        {a.AdvisoryMonth && <span>Advisory: {a.AdvisoryMonth}</span>}
                      </div>
                    </div>
                    <select
                      value={a.Status}
                      onChange={e => handleStatusChange(a.ActionKey, e.target.value)}
                      style={{
                        fontFamily:'var(--font-mono)', fontSize:10, padding:'4px 8px',
                        background:'var(--surface-2)', border:'1px solid var(--border)',
                        color: a.Status === 'DONE' ? 'var(--green)' : a.Status === 'IN_PROGRESS' ? 'var(--gold)' : 'var(--text-muted)',
                        cursor:'pointer',
                      }}
                    >
                      <option value="OPEN">Open</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="DONE">Done</option>
                    </select>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default Advisory;
